import { useState } from 'react'

import { toast } from '@buffer-mono/popcorn'

import { usePostComposer } from '~publish/hooks/usePostComposer'
import usePublishUploader from '~publish/legacy/uploads/hooks/usePublishUploader'

import { uploadInsightsImage } from './uploadInsightsImage'
import { captureSummaryAsBlob } from '../summaryCardToImage'
import type { CaptureSummaryOptions } from '../summaryCardToImage'

export interface ShareSummaryInput extends CaptureSummaryOptions {
  /** Composer prefill text. */
  text: string
  /** Alt text written to the prefilled media for accessibility. */
  altText: string
}

interface UseExportSummaryAsPostOptions {
  channelIds?: string[]
}

interface UseExportSummaryAsPostResult {
  onShare: (input: ShareSummaryInput) => Promise<void>
  isSharing: boolean
}

/**
 * Builds a "Share Summary" handler for the Insights Summary section.
 *
 * Renders the section's top stats as a branded 1080×1080 image, uploads it,
 * and opens the Post Composer pre-populated with the image + a draft blurb.
 * Sibling to {@link useExportChartAsPost}; one share action per section.
 */
export function useExportSummaryAsPost({
  channelIds,
}: UseExportSummaryAsPostOptions): UseExportSummaryAsPostResult {
  const [isSharing, setIsSharing] = useState(false)

  const { createNewPostInComposer } = usePostComposer()

  const uploader = usePublishUploader({ id: 'insights-summary-export' })

  const onShare = async (input: ShareSummaryInput): Promise<void> => {
    if (isSharing) return
    setIsSharing(true)
    try {
      const { blob, width, height } = await captureSummaryAsBlob(input)

      const file = new File([blob], 'insights-summary.png', {
        type: 'image/png',
      })
      const url = await uploadInsightsImage({
        uploader,
        file,
        sourceName: 'insights-summary',
      })

      void createNewPostInComposer({
        cta: 'publish-insights-summary-exportToPost-1',
        channels: channelIds ?? 'all',
        prefillPostData: {
          text: input.text,
          media: {
            picture: url,
            photo: url,
            thumbnail: url,
            width: String(width),
            height: String(height),
            progress: '100',
            uploaded: 'true',
            alt_text: input.altText,
          },
        },
      })
    } catch {
      toast.error('Failed to share. Please try again.')
    } finally {
      setIsSharing(false)
    }
  }

  return { onShare, isSharing }
}
